import { RoutingGroup } from "../types";
import { Metadata } from "../metadata/types";

export interface EventsQueueState {
	AlarmQueueFilters: AlarmQueueFilter[];
	AllEvents: FilteredEvent[];
	FilteredEvents: FilteredEvent[];
	CurrentSort: {
		desc: boolean;
		changed: boolean;
		sort: number;
		reversed?: boolean;
	};
	activeAlarmCount: number;
	AlarmQueueFilterIDs: AlarmQueueFilterID[];
	// Note: we should really consider merging this property with AlarmQueueFilters
	EventsLastUpdated: number;
	HoverEvent: MouseEvent;
	DisplayedEvent: FilteredEvent;
	AlarmActiveFilter: AlarmQueueFilterID;
	ManualPatrolShown: boolean;
	NewFilterModalShown: boolean;
	ActivityLogShown: boolean;
	ManualActionShown: boolean;
	SiteTree: SiteTreeNode[];
	ChangedTreeNodes: SiteTreeNode[];
	treeSelectedGroup: Group;
	selectedGroups: Group[];
	searchSelectedGroup: Group;
	queueMapBounds: google.maps.LatLngBoundsLiteral;
	eventCountsByType: {
		parked: number;
		patrols: number;
		inProcess: number;
		onTest: number;
		system: number;
	};
	eventQueueGetOverride: string;
	processingError: string | null;
	RoutingGroups: RoutingGroup[];
	AlarmTags: AlarmTag[];
	userZone: Group;
	lastEventHandled: number | null;
	ServerEventTypes: ServerEventType[];
	filteredServerEventTypes: ServerEventType[] | null;
	HandlingEvent: boolean;
	advancedTreeSearchCancelToken: any;
	JoiningEventID: any;
	TourNoteRequired: boolean;
	hideMapFlag: boolean;
	BulkHandleShown: boolean;
	AllAlarmsSelected: boolean;
	AllAlarmsClicked: boolean;
	EndEventsDialogShown: boolean;
	SelectedAlarms: number[];
	RefreshQueue: boolean;
	linkedTasksModal: {
		visible: boolean;
		event: FilteredEvent;
		responsesMetadata: { [responseId: number]: Metadata };
	};
	SystemEventRecords: SystemEventRecord[];
	HighPriorityState: HighPriorityBannerState;
	callQueue: CallQueue[];
	callClients: CallClient[];
	showIntercomModal: boolean;
	callWaitingPrefs: CallWaitingPref[];
}

interface HighPriorityBannerState {
	LoadingAccept: boolean;
	LoadingIgnore: boolean;
	Threshold: number;
	IgnoredEventIds: number[];
}

export interface AlarmQueueFilterID {
	filterId: number;
	regionId: number;
}

export interface AlarmQueueFilter {
	// Properties pulled in from the server
	alarmQueueFilterID: number;
	title: string;
	priorityHigherThan?: number;
	priorityHigherThanDDL?: string;
	numberOfEvents?: number;
	numberOfEventsDDL?: string;
	routingGroupID?: number;
	responseAlarmTypeID?: number;
	olderThan?: number;
	olderThanDDL?: string;
	userID?: number;
	shared?: boolean;
	created?: string;
	combinedFilters?: boolean;
	bounds?: {
		anyBounds: boolean;
		north: number;
		south: number;
		west: number;
		east: number;
	};
	groups?: any[];
	regionID: number;
	regionTitle?: string;
	regionalFilterID: string;

	// Computed properties
	isActive?: boolean;
	isLocal?: boolean;
	activeAlarmCount?: number;
	hide?: boolean;
	addedRegionTitle?: boolean;
}

export interface FilteredEvent {
	age: number;
	alarmCount: number;
	alarmTypeID: null;
	awaitingRestoreCount: number;
	created: string;
	displayTitle: string;
	eventID: number;
	eventTitle: string;
	eventTypeID: number;
	externalSystemInfo: null;
	groupCreated: string;
	groupID: number;
	groupTitle: string;
	groupUnparkAt: null;
	inFilters: null;
	inProcessing: boolean;
	isCCure: boolean;
	isInPostProcess: boolean;
	isTrouble: boolean;
	lastActivity: null;
	lastUnparkedAt: string;
	locations: FilteredEventLocation[];
	needsSilence: boolean;
	postProcessAttempts: number;
	priority: number;
	raisedByVideowall: boolean;
	readyForPostProcessing: Date;
	regionalEventID: string;
	regionID: number;
	regionTitle: string;
	regionURL: string;
	responses: number[];
	responseID: null;
	responseTitle: string;
	routingGroupColourCode: string;
	routingGroupID: number;
	routingGroupTitle: string;
	serverEventTitle: string;
	timezone: null;
	unParkAt: null;
	userID: null;
	userUnparkAt: null;
	username: string;
	viewedByVideowall: boolean;
	isPermanentParked: boolean;
	permanentParkPriority: number;
	permanentParkNumberOfAlarms: number;
	usersThatIgnored: number[];
	claimedUserId?: number | null;
	claimedUserFullName?: string | null;
}

export interface FilteredEventLocation {
	details: string;
	eventID: number;
	eventRecordID: number;
	latLong: string;
	location: string;
	mapLayerItemID: number;
}

/**
 * Used by the filter editor when creating new alarm queue filters.
 */
export interface AlarmQueueFilterEditModel {
	alarmQueueFilterID?: number;
	title: string;
	priorityValue?: number;
	priorityOperator?: string;
	numberOfEventsValue?: number;
	numberOfEventsOperator?: string;
	routingGroupID?: number | null;
	alarmTagID?: number | null;
	alarmAgeValue?: number;
	alarmAgeOperator?: string;
	shared?: boolean;
	created?: string;
	combinedFilters?: boolean;
	groupID?: number | null;
	inherited?: boolean;
	groups?: AlarmQueueFilterGroup[];
}

export interface AlarmQueueFilterGroup {
	alarmQueueFilterGroupsID: number;
	alarmQueueFilterID: number;
	groupID: number;
	inherited: boolean;
}

export interface AlarmTag {
	responseAlarmTypeID: number;
	title: string;
	isIncident: boolean;
	alwaysRaise: boolean;
	groupId: number;
	defaultRoutingGroupId: number;
}

export enum GroupStatus {
	All,
	Armed,
	Partial,
	Disarmed,
	OnTest
}

export interface Group {
	groupID?: number;
	parentGroupID?: number | null;
	selected?: boolean;
	timeZoneID?: number;
	title?: string;
	status?: GroupStatus;
	lastArmedDisarmedAt?: Date;
	canManualRaise?: boolean;
	address?: string | undefined | null;
	armed?: boolean;
}

export interface SiteTreeNode {
	group?: Group;
	subGroups?: SiteTreeNode[];
	isOpen?: boolean;
	show?: boolean;
	isOpenDefault?: boolean | null;
	contents?: any;
	contentsOpen?: boolean;
	loadingContents?: boolean;
	node?: SiteTreeNode;
}

export interface ServerEventType {
	serverTypeID: number;
	eventNum: number;
	title: string;
	isDefault: boolean;
	description: string;
	eventCode: string;
	restoreCode: string;
	restorable: boolean;
	restored: boolean;
	incident: boolean;
	input: boolean;
	system: boolean;
	defaultAutoHandle: boolean;
	isDuressEvent: boolean;
	defaultPriority: number;
	serverTypeEventProtocolTypeId: number;
	input1Label: string;
	input2Label: string;
	defaultIgnore: boolean;
	ignoreDefaultAlarm: boolean;
	defaultTag: number;
	defaultScript: number;
	matchRadius: number;
	matchGeofence: boolean;
	duressServerEventTypeTitle: string | null;
}

export interface SiteTreeNodeValues {
	treeNode: SiteTreeNode;
	isOpen?: boolean;
	selected?: boolean;
	contents?: any;
	loadingContents?: boolean;
	contentsOpen?: boolean;
}

export interface LoadServerEventTypesParams {
	serverType: number;
	groupID: number | null;
	pageSize?: number | null;
	regionId?: number | null;
}

export interface SearchServerEventTypesParams extends LoadServerEventTypesParams {
	filter: string;
}

export interface SystemEventRecord {
	count: number;
	details: string;
	eventId: number;
	eventRecordIds: number[];
	firstAlarm: Date;
	input1: number | null;
	input2: number | null;
	lastAlarm: Date;
}

export interface HighPriorityAcceptRequest {
	currentEventId: number;
	currentEventRegionId: number;
	newEventId: number;
	newEventRegionId: number;
}

export interface HighPriorityIgnoreRequest {
	eventId: number;
	eventRegionId: number;
	eventRegionUrl: string;
}

export interface HandleEventResult {
	eventID: number;
	eventUserID?: number;
	inProcessing: boolean;
	canProcess: boolean;
	removedFromEvent: boolean;
	externalErrors: string[];
	regionID?: number;
	regionURL: string;
	eventTypeId: number;
}

export enum EventType {
	OnTest = 6
}

export interface CallQueue {
	callId: number;
	callerIdentifier: string;
	clientId: number;
	created: string;
	pickedUp?: string;
	deviceId?: number;
	eventId: number;
	groupId?: number;
	serverTypeId: number;
	deviceEndPoint?: string;
	title?: string;
}

export interface CallClient {
	clientId: number;
	identifier?: string;
	serverTypeId: number;
	userId?: number;
}

export interface PickupCall {
	userId: number;
	callId: number;
	eventId: number;
	areaName: string;
	deviceId: number;
	deviceEndPoint: string;
	title: string;
}

export interface CallWaitingPref {
	prefId: number;
	prefValue: number | null;
}
